'use strict'
import * as self from './plotly.js'
import _ from 'lodash'

/*
██████  ██       ██████  ████████ ██   ██    ██     ██████   █████  ████████  █████
██   ██ ██      ██    ██    ██    ██    ██  ██      ██   ██ ██   ██    ██    ██   ██
██████  ██      ██    ██    ██    ██     ████       ██   ██ ███████    ██    ███████
██      ██      ██    ██    ██    ██      ██        ██   ██ ██   ██    ██    ██   ██
██      ███████  ██████     ██    ███████ ██        ██████  ██   ██    ██    ██   ██
*/

export class PlotlyData {
  constructor (name, x, y, hoverText = null, color = null) {
    this.hoverinfo = 'x+y+z+text+name'
    this.hovertext = hoverText
    this.line = {
      color: color,
      dash: 'solid',
      shape: 'linear',
      width: 1
    }
    this.marker = {
      color: color,
      line: {
        width: 0
      },
      opacity: 1,
      size: 6,
      symbol: 'circle'
    }
    this.mode = 'lines'
    this.name = name
    this.text = null
    this.type = 'scatter'
    this.visible = true
    this.x = x
    this.y = y

    this.checkDataLength()
  }

  hideHoverData () {
    this.hoverinfo = 'x'
  }

  setColorFromTypeId () {
    let color = null
    this.line.color = color
    this.marker.color = color
  }

  hide () {
    this.visible = 'legendonly'
  }

  showPoint () {
    this.mode = 'lines+markers'
  }

  showOnlyText () {
    this.hoverinfo = 'text'
  }

  showStartEnd (firstText = null, lastText = null, azimuth = null) {
    this.mode = 'lines+markers'

    let markerSize = this.marker.size
    let markerSizeBig = 2 * markerSize
    let markerSymbol = this.marker.symbol

    markerSize = _.times(this.x.length, _.constant(markerSize))
    markerSize.splice(0, 1, markerSizeBig)
    markerSize.splice(markerSize.length - 1, 1, markerSizeBig)
    this.marker.size = markerSize

    markerSymbol = _.times(this.x.length, _.constant(markerSymbol))
    markerSymbol.splice(0, 1, 'circle-open-dot')
    this.marker.symbol = markerSymbol

    if (firstText && lastText && azimuth) {
      let firstPosition
      let lastPosition

      if (azimuth > 337.5 || azimuth <= 22.5) {
        firstPosition = 'bottom center'
        lastPosition = 'top center'
      } else if (azimuth > 22.5 && azimuth <= 67.5) {
        firstPosition = 'bottom left'
        lastPosition = 'top right'
      } else if (azimuth > 67.5 && azimuth <= 112.5) {
        firstPosition = 'middle left'
        lastPosition = 'middle right'
      } else if (azimuth > 112.5 && azimuth <= 157.5) {
        firstPosition = 'top left'
        lastPosition = 'bottom right'
      } else if (azimuth > 157.5 && azimuth <= 202.5) {
        firstPosition = 'top center'
        lastPosition = 'bottom center'
      } else if (azimuth > 202.5 && azimuth <= 247.5) {
        firstPosition = 'top right'
        lastPosition = 'bottom left'
      } else if (azimuth > 247.5 && azimuth <= 292.5) {
        firstPosition = 'middle right'
        lastPosition = 'middle left'
      } else if (azimuth > 292.5 && azimuth <= 337.5) {
        firstPosition = 'bottom right'
        lastPosition = 'top left'
      }

      this.mode = 'lines+markers+text'
      let text = _.times(this.x.length, _.constant(''))
      let textPosition = _.times(this.x.length, _.constant('middle center'))
      text.splice(0, 1, firstText)
      text.splice(text.length - 1, 1, lastText)
      textPosition.splice(0, 1, firstPosition)
      textPosition.splice(text.length - 1, 1, lastPosition)
      this.text = text
      this.textposition = textPosition
    }
  }

  // NOTE Utiliser du minimum au maximum, dans cette ordre!
  isZoneMin () {
    this.line.width = 1
    this.line.color = '#ddd'
    this.legendgroup = 1
  }

  isZone25 () {
    this.fill = 'tonexty'
    this.fillcolor = '#eee'
    this.line.width = 1
    this.line.color = '#bbb'
    this.legendgroup = 1
  }

  isZoneMedian () {
    this.fill = 'tonexty'
    this.fillcolor = '#ccc'
    this.line.width = 1
    this.line.color = '#666'
    this.legendgroup = 1
  }

  isZone75 () {
    this.fill = 'tonexty'
    this.fillcolor = '#ccc'
    this.line.width = 1
    this.line.color = '#bbb'
    this.legendgroup = 1
  }

  isZoneMax () {
    this.fill = 'tonexty'
    this.fillcolor = '#eee'
    this.line.width = 1
    this.line.color = '#ddd'
    this.legendgroup = 1
  }

  // UTILS

  checkDataLength () {
    if (this.x.length !== this.y.length) {
      throw new Error('le nombre de valeurs entre `x` et `y` n’est pas correcte')
    }

    if (_.isArray(this.text) && this.text.length > 0 && this.text.length !== this.x.length) {
      throw new Error('le nombre de valeurs entre `textes` et `x` n’est pas correcte')
    }

    if (_.isArray(this.hovertext) && this.hovertext.length > 0 && this.hovertext.length !== this.x.length) {
      throw new Error('le nombre de valeurs entre `textes` et `x` n’est pas correcte')
    }
  }

  yAxis (yAxis) {
    this.yaxis = Number.isInteger(yAxis) ? this.axis(yAxis) : yAxis
  }

  axis (number, name = 'y') {
    if (!_.isInteger(number)) {
      return new Error('le numéro de l’axe n’est pas un nombre')
    }
    return number !== 0 ? name + (number + 1) : name
  }

  toObject () {
    return JSON.parse(JSON.stringify(this))
  }
}

/*
██████  ██       ██████  ████████ ██   ██    ██      █████  ██   ██ ██ ███████
██   ██ ██      ██    ██    ██    ██    ██  ██      ██   ██  ██ ██  ██ ██
██████  ██      ██    ██    ██    ██     ████       ███████   ███   ██ ███████
██      ██      ██    ██    ██    ██      ██        ██   ██  ██ ██  ██      ██
██      ███████  ██████     ██    ███████ ██        ██   ██ ██   ██ ██ ███████
*/

class PlotlyAxis {
  constructor (title = '', tickSuffix = '') {
    this._isDate = false
    this._noYear = false
    this._noYearHydrological = false
    this.exponentformat = 'none'
    this.fixedrange = false
    this.gridcolor = '#ddd'
    this.hoverformat = ''
    this.linewidth = 2
    this.linecolor = '#aaa'
    this.mirror = true
    this.scaleanchor = null
    this.showgrid = true
    this.showline = true
    this.showticksuffix = false
    this.side = 'left'
    this.tickformatstops = []
    this.ticks = 'outside'
    this.ticksuffix = tickSuffix
    this.title = title
  }

  isDateByGranularity (granularity) {
    if (granularity === 'daily') {
      this.isDateWithoutHour()
    } else {
      this.isDate()
    }
  }

  isDate () {
    this._isDate = true
    this.hoverformat = PLOTLY_DATE_TIME
    this.tickformatstops = PLOTLY_DATES
  }

  isDateWithoutHour () {
    this._isDate = true
    this.hoverformat = PLOTLY_DATE
    this.tickformatstops = PLOTLY_DATES
  }

  isMonthsOfYear () {
    this._isDate = true
    this._noYear = true
    this.hoverformat = PLOTLY_MONTH
    this.tickformatstops = PLOTLY_DATES_WITHOUT_YEAR
  }

  isDaysOfYear (hydrologicalYear = false) {
    this._isDate = true
    if (hydrologicalYear) {
      this._noYearHydrological = true
    } else {
      this._noYear = true
    }
    this.hoverformat = PLOTLY_DATE_WITHOUT_YEAR
    this.tickformatstops = PLOTLY_DATES_WITHOUT_YEAR
  }

  setRange (min, max) {
    this.range = [min, max]
  }

  sameScale (axis = 'x') {
    this.scaleanchor = axis
  }
}

export class PlotlyXAxis extends PlotlyAxis {
  constructor (title, suffix) {
    super(title, suffix)
    this._orientation = 'x'
  }
}

export class PlotlyYAxis extends PlotlyAxis {
  constructor (title, suffix) {
    super(title, suffix)
    this._orientation = 'y'
  }
}

// this.rangeselector = {
//   visible: false,
//   buttons: [
//     {
//       count: 7,
//       label: '1 semaine',
//       step: 'day',
//       stepmode: 'backward'
//     },
//     {
//       count: 1,
//       label: '1 mois',
//       step: 'month',
//       stepmode: 'backward'
//     },
//     {
//       count: 6,
//       label: '6 mois',
//       step: 'month',
//       stepmode: 'backward'
//     },
//     {
//       count: 1,
//       label: '1 année',
//       step: 'year',
//       stepmode: 'backward'
//     },
//     {
//       label: 'tout',
//       step: 'all'
//     }
//   ]
// }
// this.rangeslider = {
//   visible: flase
// }

/*
██████  ██       ██████  ████████ ██   ██    ██     ██       █████  ██    ██  ██████  ██    ██ ████████
██   ██ ██      ██    ██    ██    ██    ██  ██      ██      ██   ██  ██  ██  ██    ██ ██    ██    ██
██████  ██      ██    ██    ██    ██     ████       ██      ███████   ████   ██    ██ ██    ██    ██
██      ██      ██    ██    ██    ██      ██        ██      ██   ██    ██    ██    ██ ██    ██    ██
██      ███████  ██████     ██    ███████ ██        ███████ ██   ██    ██     ██████   ██████     ██
*/

export class PlotlyLayout {
  constructor () {
    this._lastXAxis = 0
    this._lastYAxis = 0
    this._showHover = false
    // NOTE annotations[0] réservé pour le texte de now
    this.annotations = [null]
    this.hovermode = 'x'
    this.legend = {
      orientation: 'h',
      bgcolor: 'transparent',
      // BUG Problème de place pour la légende https://github.com/plotly/plotly.js/issues/1199
      // Résolution temporaire (en écartant plus la légende quand horizontal)
      // NOTE Penser à la fonction `legendVertical`
      y: -0.2
    }
    this.margin = {
      t: 30,
      b: 30
    }
    this.plot_bgcolor = '#fcfcfc'
    this.separators = ".'"
    // NOTE shapes[0] réservé pour la ligne du hover et shapes[1] pour le now
    this.shapes = [null, null]
    this.showlegend = true
  }

  addAxis (axis) {
    if (!(axis instanceof PlotlyAxis)) {
      throw new Error('l’axe doit être un `PlotlyXAxis` ou `PlotlyYAxis`')
    }
    let orientation = axis._orientation
    if (this._lastYAxis > 0) {
      axis.overlaying = axis._orientation
    }
    this[this[`${orientation}Axis`]()] = axis
  }

  displayNow () {
    this._displayNow = true
  }

  thresholds (thresholds) {
    _.forEach(thresholds, threshold => {
      this.shapes.push({
        type: 'line',
        xref: 'paper',
        yref: 'y',
        x0: 0,
        y0: threshold.value,
        x1: 1,
        y1: threshold.value,
        line: {
          color: threshold.color
        }
      })

      if (threshold.text) {
        this.annotations.push({
          xref: 'paper',
          yref: 'y',
          x: 1,
          y: threshold.value,
          text: threshold.text,
          showarrow: false,
          xanchor: 'right',
          yanchor: 'bottom',
          bgcolor: 'rgba(255, 255, 255, .5)'
        })
      }
    })
  }

  lines (lines) {
    _.forEach(lines, line => {
      this.shapes.push({
        type: 'line',
        xref: 'paper',
        x0: 0,
        y0: line.value,
        x1: 1,
        y1: line.value,
        line: {
          width: 1,
          color: line.color || null
        }
      })
    })
  }

  displayHoverLine () {
    this._showHover = true
  }

  selectClosest () {
    this.hovermode = 'closest'
  }

  xAxis () {
    let name = 'xaxis'
    return ++this._lastXAxis === 1 ? name : name + this._lastXAxis
  }

  yAxis () {
    let name = 'yaxis'
    return ++this._lastYAxis === 1 ? name : name + this._lastYAxis
  }

  toObject () {
    return JSON.parse(JSON.stringify(this))
  }

  legendVertical () {
    this.legend.orientation = 'v'
    this.legend.y = null
  }
}

const PLOTLY_DATE_TIME = '%d.%m.%Y %H:%M'
const PLOTLY_DATE = '%d.%m.%Y'
const PLOTLY_DATE_WITHOUT_YEAR = '%d.%m'
const PLOTLY_MONTH = '%B'

// Documentation sur l'affichage des dates
// https://github.com/d3/d3-time-format/blob/master/README.md#locale_format

const PLOTLY_DATES_WITHOUT_YEAR = [
  {
    // milliseconds
    dtickrange: [null, 1000],
    value: '%H:%M:%S.%L\n%d.%m'
  },
  {
    // secondes
    dtickrange: [null, 60000],
    value: '%H:%M:%S\n%d.%m'
  },
  {
    // minutes
    dtickrange: [null, 3600000],
    value: '%H:%M\n%d.%m'
  },
  {
    // heures
    dtickrange: [null, 82800000],
    value: '%H:%M\n%d.%m'
  },
  {
    // jour
    dtickrange: [null, 2592000000],
    value: '%d.%m'
  },
  {
    // mois
    dtickrange: [null, 'M12'],
    value: '%B'
  },
  {
    // années
    dtickrange: [null, null],
    value: ' '
  }
]

const PLOTLY_DATES = [
  {
    // milliseconds
    dtickrange: [null, 1000],
    value: '%H:%M:%S.%L\n%d.%m.%y'
  },
  {
    // secondes
    dtickrange: [null, 60000],
    value: '%H:%M:%S\n%d.%m.%y'
  },
  {
    // minutes
    dtickrange: [null, 3600000],
    value: '%H:%M\n%d.%m.%y'
  },
  {
    // heures
    dtickrange: [null, 82800000],
    value: '%H:%M\n%d.%m.%y'
  },
  {
    // jour
    dtickrange: [null, 2592000000],
    value: '%d.%m.%y'
  },
  {
    // mois
    dtickrange: [null, 'M11'],
    value: '%m.%Y'
  },
  {
    // années
    dtickrange: [null, null],
    value: '%Y'
  }
]

export default self
